import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import RestoreIcon from '@mui/icons-material/Restore';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import SettingsPageHeader from 'components/SettingsPageHeader';
import TemplateGroupView from 'pages/contract/components/TemplateGroupView';
import {
  ContractTemplateGroupResult,
  ContractTemplateItemResult,
  ContractTemplateUnassignedItemResult,
  CreateSelectItemInput,
  GetContractTemplateResponse,
  UpdateSelectItemInput,
  createContractTemplateGroup,
  createContractTemplateItem,
  deleteContractTemplateItem,
  getContractTemplate,
  updateContractTemplateGroup,
  updateContractTemplateGroupDisplay,
  updateContractTemplateItem,
  updateContractTemplateItemDisplay,
} from 'stores/ContractTemplateStoreAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

// --- FormType definitions ---
const SELECT_FORM_TYPES = [
  'SELECT',
  'MULTI_SELECT',
  'RADIO',
  'CHECK',
  'MULTI_CHECK',
  'MULTI_CHECK_BUTTON',
];

const FORM_TYPE_OPTIONS = [
  { code: 'TEXT', label: 'テキスト' },
  { code: 'KANA_TEXT', label: 'カナテキスト' },
  { code: 'NUMBER_TEXT', label: '数値テキスト' },
  { code: 'SELECT', label: 'セレクト' },
  { code: 'MULTI_SELECT', label: 'マルチセレクト' },
  { code: 'RADIO', label: 'ラジオ' },
  { code: 'CHECK', label: 'チェック' },
  { code: 'MULTI_CHECK', label: 'マルチチェック' },
  { code: 'MULTI_CHECK_BUTTON', label: 'マルチチェックボタン' },
  { code: 'DATE_PICKER_START', label: '日付ピッカー（開始）' },
  { code: 'DATE_PICKER_END', label: '日付ピッカー（終了）' },
  { code: 'TECH_STACK_SELECT', label: '技術スタックセレクト' },
];

// --- Types ---
type SelectedNode =
  | { type: 'group'; groupId: string }
  | { type: 'item'; itemKey: string; groupId: string | null }
  | { type: 'new-item'; groupId: string | null }
  | null;

// --- Drop Indicator ---
const DropIndicatorLine: React.FC = () => (
  <Box
    sx={{
      position: 'relative',
      height: 4,
      mx: 1,
      my: 0.5,
      display: 'flex',
      alignItems: 'center',
    }}
  >
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        flexShrink: 0,
      }}
    />
    <Box
      sx={{
        flex: 1,
        height: 2,
        bgcolor: 'primary.main',
        borderRadius: 1,
      }}
    />
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: '50%',
        bgcolor: 'primary.main',
        flexShrink: 0,
      }}
    />
  </Box>
);

// --- Main Component ---
const TemplateManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<GetContractTemplateResponse | null>(null);
  const [selectedNode, setSelectedNode] = useState<SelectedNode>(null);

  const fetchData = useCallback(() => {
    setLoading(true);
    getContractTemplate({ visibleOnly: false })
      .then((res) => {
        setData(res);
        setLoading(false);
      })
      .catch(() => {
        errorDefault('テンプレートの取得に失敗しました');
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddGroup = () => {
    createContractTemplateGroup({ name: '新しいグループ' })
      .then(() => {
        successDefault('グループを追加しました');
        fetchData();
      })
      .catch((e: Error) => {
        errorDefault('グループの追加に失敗しました', e.message);
      });
  };

  if (loading || !data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SettingsPageHeader
        breadcrumbs={[{ label: '契約管理', to: '/contract' }, { label: '申請フォーム設定' }]}
        icon={<ViewQuiltIcon />}
        title="申請フォーム設定"
        description="申請種別ごとの入力フォーム（グループ・項目）を設定します"
        actions={
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddGroup}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            グループ追加
          </Button>
        }
      />

      <Grid container spacing={1.5} sx={{ flex: 1, overflow: 'hidden' }}>
        <Grid size={5} sx={{ height: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'auto',
              height: '100%',
            }}
          >
            <LeftPane
              data={data}
              selectedNode={selectedNode}
              onSelectNode={setSelectedNode}
              onRefresh={fetchData}
              onDataChange={setData}
            />
          </Box>
        </Grid>
        <Grid size={7} sx={{ height: '100%', overflow: 'hidden' }}>
          <Box
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'auto',
              height: '100%',
            }}
          >
            <RightPane
              data={data}
              selectedNode={selectedNode}
              onRefresh={fetchData}
              onCancelNewItem={() => setSelectedNode(null)}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

// --- Helper: find item across all groups and unassigned ---
const findItemInData = (
  data: GetContractTemplateResponse,
  itemKey: string,
): ContractTemplateItemResult | null => {
  for (const g of data.results) {
    const found = g.items.find((i) => i.itemKey === itemKey);
    if (found) return found;
  }
  // Check unassigned (convert to ItemResult shape)
  const unassigned = data.unassignedItems.find((i) => i.itemKey === itemKey);
  if (unassigned) {
    return {
      itemKey: unassigned.itemKey,
      label: unassigned.label,
      formType: unassigned.formType,
      sortOrder: -1,
      isDeleted: unassigned.isDeleted,
      selectItems: unassigned.selectItems || [],
    };
  }
  return null;
};

// --- Left Pane ---
type LeftPaneProps = {
  data: GetContractTemplateResponse;
  selectedNode: SelectedNode;
  onSelectNode: (node: SelectedNode) => void;
  onRefresh: () => void;
  onDataChange: (data: GetContractTemplateResponse) => void;
};

const LeftPane: React.FC<LeftPaneProps> = ({
  data,
  selectedNode,
  onSelectNode,
  onRefresh,
  onDataChange,
}) => {
  const [showHiddenGroups, setShowHiddenGroups] = useState(true);
  const [showDeletedItems, setShowDeletedItems] = useState(false);

  const visibleGroups = useMemo(
    () => data.results.filter((g) => g.sortOrder >= 0).sort((a, b) => a.sortOrder - b.sortOrder),
    [data.results],
  );

  const hiddenGroups = useMemo(() => data.results.filter((g) => g.sortOrder < 0), [data.results]);

  const unassignedActive = useMemo(
    () => data.unassignedItems.filter((i) => !i.isDeleted),
    [data.unassignedItems],
  );

  const deletedItems = useMemo(
    () => data.unassignedItems.filter((i) => i.isDeleted),
    [data.unassignedItems],
  );

  const [deleteTarget, setDeleteTarget] = useState<ContractTemplateUnassignedItemResult | null>(
    null,
  );

  const [groupDragIndex, setGroupDragIndex] = useState<number | null>(null);
  const [groupDragOverIndex, setGroupDragOverIndex] = useState<number | null>(null);

  const handleGroupDragStart = (index: number) => {
    setGroupDragIndex(index);
  };

  const handleGroupDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    setGroupDragOverIndex(e.clientY < midY ? index : index + 1);
  };

  const handleGroupDrop = () => {
    if (groupDragIndex === null || groupDragOverIndex === null) {
      setGroupDragIndex(null);
      setGroupDragOverIndex(null);
      return;
    }
    let targetIndex = groupDragOverIndex;
    if (targetIndex > groupDragIndex) targetIndex -= 1;
    if (groupDragIndex === targetIndex) {
      setGroupDragIndex(null);
      setGroupDragOverIndex(null);
      return;
    }
    const reordered = [...visibleGroups];
    const [moved] = reordered.splice(groupDragIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const groups = reordered.map((g, i) => ({ groupId: g.groupId, sortOrder: i }));

    // Optimistic update
    const updatedResults = data.results.map((g) => {
      const entry = groups.find((ge) => ge.groupId === g.groupId);
      if (entry) return { ...g, sortOrder: entry.sortOrder };
      // Groups not in the visible list keep their sortOrder (hidden groups stay -1)
      return g;
    });
    onDataChange({ ...data, results: updatedResults });

    updateContractTemplateGroupDisplay({ groups }).catch((e: Error) => {
      errorDefault('並び替えの保存に失敗しました', toErrorDetail(e));
      onRefresh();
    });

    setGroupDragIndex(null);
    setGroupDragOverIndex(null);
  };

  const handleItemDrop = (
    itemKey: string,
    sourceGroupId: string | null,
    targetGroupId: string | null,
    targetIndex: number,
  ) => {
    // Build the full item list for the target group
    const targetGroup = data.results.find((g) => g.groupId === targetGroupId);
    const targetItems = targetGroup
      ? targetGroup.items.filter((i) => !i.isDeleted).sort((a, b) => a.sortOrder - b.sortOrder)
      : [];

    // Remove the dragged item from target list if it was already there
    const filteredTarget = targetItems.filter((i) => i.itemKey !== itemKey);
    // Insert at target index
    filteredTarget.splice(targetIndex, 0, { itemKey } as ContractTemplateItemResult);

    // Build display update request
    const items = filteredTarget.map((item, i) => ({
      itemKey: item.itemKey,
      groupId: targetGroupId,
      sortOrder: i + 1,
    }));

    // If moving from another group, also set the dragged item
    if (sourceGroupId !== targetGroupId) {
      const existingEntry = items.find((i) => i.itemKey === itemKey);
      if (!existingEntry) {
        items.push({ itemKey, groupId: targetGroupId, sortOrder: targetIndex + 1 });
      }
    }

    // If moving to unassigned
    if (targetGroupId === null) {
      // Optimistic update: move item to unassigned
      const updatedResults = data.results.map((g) => ({
        ...g,
        items: g.items
          .map((i) => (i.itemKey === itemKey ? { ...i, sortOrder: -1 } : i))
          .filter((i) => i.itemKey !== itemKey),
      }));
      const movedItem = findItemInData(data, itemKey);
      const updatedUnassigned = movedItem
        ? [
            ...data.unassignedItems,
            {
              itemKey: movedItem.itemKey,
              label: movedItem.label,
              formType: movedItem.formType,
              isDeleted: false,
              selectItems: movedItem.selectItems || [],
            },
          ]
        : data.unassignedItems;
      onDataChange({ results: updatedResults, unassignedItems: updatedUnassigned });

      updateContractTemplateItemDisplay({
        items: [{ itemKey, groupId: null, sortOrder: -1 }],
      }).catch((e: Error) => {
        errorDefault('移動の保存に失敗しました', toErrorDetail(e));
        onRefresh();
      });
      return;
    }

    // Optimistic update: move item between groups or reorder within group
    const movedItem = findItemInData(data, itemKey);
    const updatedResults = data.results.map((g) => {
      // Remove item from source group
      let groupItems = g.items.filter((i) => i.itemKey !== itemKey);
      // Add to target group with new sort orders
      if (g.groupId === targetGroupId && movedItem) {
        const newItem = { ...movedItem, sortOrder: targetIndex + 1 };
        groupItems = groupItems.filter((i) => i.itemKey !== itemKey);
        groupItems.push(newItem);
        // Re-sort
        groupItems = groupItems
          .filter((i) => !i.isDeleted)
          .map((i, idx) => {
            const entry = items.find((e) => e.itemKey === i.itemKey);
            return entry ? { ...i, sortOrder: entry.sortOrder } : i;
          })
          .concat(g.items.filter((i) => i.isDeleted));
      }
      return { ...g, items: groupItems };
    });
    // Remove from unassigned if it was there
    const updatedUnassigned = data.unassignedItems.filter((i) => i.itemKey !== itemKey);
    onDataChange({ results: updatedResults, unassignedItems: updatedUnassigned });

    updateContractTemplateItemDisplay({ items }).catch((e: Error) => {
      errorDefault('移動の保存に失敗しました', toErrorDetail(e));
      onRefresh();
    });
  };

  const handleDeleteItem = () => {
    if (!deleteTarget) return;
    deleteContractTemplateItem({ itemKey: deleteTarget.itemKey })
      .then(() => {
        successDefault('削除しました');
        setDeleteTarget(null);
        onRefresh();
      })
      .catch((e: Error) => {
        errorDefault('削除に失敗しました', e.message);
        setDeleteTarget(null);
      });
  };

  return (
    <Box sx={{ p: 1.5 }}>
      {/* Filter toggles */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, px: 0.5 }}>
        <Chip
          size="small"
          label="非表示グループ"
          variant={showHiddenGroups ? 'filled' : 'outlined'}
          onClick={() => setShowHiddenGroups((v) => !v)}
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            cursor: 'pointer',
            ...(showHiddenGroups
              ? { bgcolor: 'text.secondary', color: 'white' }
              : { borderColor: 'text.disabled', color: 'text.disabled' }),
          }}
        />
        <Chip
          size="small"
          label="削除済み"
          variant={showDeletedItems ? 'filled' : 'outlined'}
          onClick={() => setShowDeletedItems((v) => !v)}
          sx={{
            height: 22,
            fontSize: '0.7rem',
            fontWeight: 600,
            cursor: 'pointer',
            ...(showDeletedItems
              ? { bgcolor: 'text.secondary', color: 'white' }
              : { borderColor: 'text.disabled', color: 'text.disabled' }),
          }}
        />
      </Stack>

      {/* Visible groups */}
      {visibleGroups.map((group, index) => (
        <React.Fragment key={group.groupId}>
          {groupDragOverIndex === index && groupDragIndex !== null && groupDragIndex !== index && (
            <DropIndicatorLine />
          )}
          <GroupTreeNode
            group={group}
            selectedNode={selectedNode}
            onSelectNode={onSelectNode}
            onRefresh={onRefresh}
            onItemDrop={handleItemDrop}
            draggable
            onDragStart={() => handleGroupDragStart(index)}
            onDragOver={(e) => handleGroupDragOver(e, index)}
            onDrop={handleGroupDrop}
            dragIndex={groupDragIndex}
            index={index}
          />
        </React.Fragment>
      ))}
      {groupDragOverIndex === visibleGroups.length && groupDragIndex !== null && (
        <DropIndicatorLine />
      )}

      {/* Hidden groups */}
      {showHiddenGroups &&
        hiddenGroups.map((group) => (
          <GroupTreeNode
            key={group.groupId}
            group={group}
            selectedNode={selectedNode}
            onSelectNode={onSelectNode}
            onRefresh={onRefresh}
            onItemDrop={handleItemDrop}
            draggable={false}
          />
        ))}

      {/* Unassigned items */}
      <Accordion elevation={0} defaultExpanded sx={{ '&:before': { display: 'none' }, border: 0 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, minHeight: 36 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              未所属item ({unassignedActive.length})
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSelectNode({ type: 'new-item', groupId: null });
              }}
              sx={{ color: 'primary.main' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 1, pt: 0 }}>
          <UnassignedItemsList
            items={unassignedActive}
            selectedNode={selectedNode}
            onSelectNode={onSelectNode}
            onDelete={setDeleteTarget}
            onItemDrop={handleItemDrop}
          />
        </AccordionDetails>
      </Accordion>

      {/* Deleted items */}
      {showDeletedItems && deletedItems.length > 0 && (
        <Accordion
          elevation={0}
          defaultExpanded={false}
          sx={{ '&:before': { display: 'none' }, border: 0 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 1, minHeight: 36 }}>
            <Typography variant="body2" color="text.secondary">
              削除済みitem ({deletedItems.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pt: 0 }}>
            <Stack spacing={0}>
              {deletedItems.map((item) => (
                <Box
                  key={item.itemKey}
                  sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}
                >
                  <Box sx={{ width: 28 }} />
                  <Typography variant="body2" color="text.disabled">
                    {item.label}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        fullWidth
        maxWidth="xs"
        PaperProps={{ elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } }}
      >
        <DialogTitle>
          <Typography variant="subtitle1" fontWeight={600}>
            削除しますか？
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            「{deleteTarget?.label}
            」を削除します。削除済み項目として残りますが、利用できなくなります。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteItem}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// --- GroupTreeNode ---
type GroupTreeNodeProps = {
  group: ContractTemplateGroupResult;
  selectedNode: SelectedNode;
  onSelectNode: (node: SelectedNode) => void;
  onRefresh: () => void;
  onItemDrop: (
    itemKey: string,
    sourceGroupId: string | null,
    targetGroupId: string | null,
    targetIndex: number,
  ) => void;
  draggable: boolean;
  onDragStart?: () => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: () => void;
  dragIndex?: number | null;
  index?: number;
};

const GroupTreeNode: React.FC<GroupTreeNodeProps> = ({
  group,
  selectedNode,
  onSelectNode,
  onRefresh,
  onItemDrop,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  dragIndex,
  index,
}) => {
  const isFixed = group.templateCategory === 'FIXED';
  const isVisible = group.sortOrder >= 0;
  const activeItems = useMemo(
    () => group.items.filter((i) => !i.isDeleted).sort((a, b) => a.sortOrder - b.sortOrder),
    [group.items],
  );

  const isGroupSelected = selectedNode?.type === 'group' && selectedNode.groupId === group.groupId;

  const [itemDragIndex, setItemDragIndex] = useState<number | null>(null);
  const [itemDragOverIndex, setItemDragOverIndex] = useState<number | null>(null);

  const handleItemDragStart = (e: React.DragEvent, itemKey: string, idx: number) => {
    e.stopPropagation();
    setItemDragIndex(idx);
    e.dataTransfer.setData('text/plain', JSON.stringify({ itemKey, sourceGroupId: group.groupId }));
  };

  const handleItemDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    setItemDragOverIndex(e.clientY < midY ? idx : idx + 1);
  };

  const handleItemDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
      const targetIdx = itemDragOverIndex ?? activeItems.length;
      onItemDrop(dragData.itemKey, dragData.sourceGroupId, group.groupId, targetIdx);
    } catch {
      // ignore invalid data
    }
    setItemDragIndex(null);
    setItemDragOverIndex(null);
  };

  const handleGroupAreaDragOver = (e: React.DragEvent) => {
    if (!isFixed) {
      e.preventDefault();
      e.stopPropagation();
      if (itemDragOverIndex === null) {
        setItemDragOverIndex(activeItems.length);
      }
    }
  };

  return (
    <Accordion
      elevation={0}
      defaultExpanded={isVisible}
      sx={{
        '&:before': { display: 'none' },
        border: 0,
        opacity: draggable && dragIndex === index ? 0.4 : 1,
      }}
      draggable={draggable}
      onDragStart={(e) => {
        if (draggable && onDragStart) {
          e.stopPropagation();
          onDragStart();
        }
      }}
      onDragOver={(e) => {
        if (draggable && onDragOver) onDragOver(e);
        handleGroupAreaDragOver(e);
      }}
      onDrop={(e) => {
        if (onDrop) onDrop();
        handleItemDrop(e);
      }}
      onDragEnd={() => {
        setItemDragIndex(null);
        setItemDragOverIndex(null);
      }}
      onDragLeave={() => {
        setItemDragOverIndex(null);
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          px: 1.5,
          minHeight: 40,
          bgcolor: isGroupSelected ? 'action.selected' : 'transparent',
          borderRadius: 1,
          borderLeft: 3,
          borderColor: isGroupSelected
            ? 'primary.main'
            : isVisible
              ? 'success.main'
              : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSelectNode({ type: 'group', groupId: group.groupId });
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          {draggable && (
            <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', cursor: 'grab' }} />
          )}
          {isVisible ? (
            <FolderIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          ) : (
            <FolderOffIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          )}
          <Typography variant="body2" fontWeight={600} noWrap>
            {group.name}
          </Typography>
          <Chip
            size="small"
            label={isVisible ? '表示中' : '非表示'}
            variant={isVisible ? 'filled' : 'outlined'}
            sx={{
              height: 18,
              fontSize: '0.65rem',
              fontWeight: 600,
              ...(isVisible
                ? { bgcolor: 'success.main', color: 'white' }
                : { borderColor: 'text.disabled', color: 'text.disabled' }),
            }}
          />
          <Box sx={{ flex: 1 }} />
          {!isFixed && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onSelectNode({ type: 'new-item', groupId: group.groupId });
              }}
              sx={{ color: 'primary.main' }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 0.5 }}>
        <Stack spacing={0}>
          {activeItems.map((item, idx) => (
            <React.Fragment key={item.itemKey}>
              {itemDragOverIndex === idx && itemDragOverIndex !== itemDragIndex && (
                <DropIndicatorLine />
              )}
              <ItemRow
                item={item}
                groupId={group.groupId}
                isFixed={isFixed}
                isSelected={selectedNode?.type === 'item' && selectedNode.itemKey === item.itemKey}
                onSelect={() =>
                  onSelectNode({ type: 'item', itemKey: item.itemKey, groupId: group.groupId })
                }
                draggable={!isFixed}
                onDragStart={(e) => handleItemDragStart(e, item.itemKey, idx)}
                onDragOver={(e) => handleItemDragOver(e, idx)}
              />
            </React.Fragment>
          ))}
          {itemDragOverIndex === activeItems.length && itemDragOverIndex !== itemDragIndex && (
            <DropIndicatorLine />
          )}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

// --- ItemRow ---
type ItemRowProps = {
  item: ContractTemplateItemResult;
  groupId: string;
  isFixed: boolean;
  isSelected: boolean;
  onSelect: () => void;
  draggable: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
};

const ItemRow: React.FC<ItemRowProps> = ({
  item,
  groupId,
  isFixed,
  isSelected,
  onSelect,
  draggable,
  onDragStart,
  onDragOver,
}) => {
  return (
    <Box
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onClick={onSelect}
      sx={{
        display: 'flex',
        alignItems: 'center',
        pl: 2.5,
        pr: 1,
        py: 0.75,
        ml: 1,
        borderRadius: 1,
        cursor: 'pointer',
        bgcolor: isSelected ? 'action.selected' : 'transparent',
        borderLeft: isSelected ? 2 : 0,
        borderColor: 'primary.main',
        '&:hover': { bgcolor: 'action.hover' },
        transition: 'background-color 0.2s',
      }}
    >
      {draggable ? (
        <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'grab', mr: 1 }} />
      ) : (
        <Box sx={{ width: 22 }} />
      )}
      <TextFieldsIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 1 }} />
      <Typography variant="body2" sx={{ flex: 1 }} noWrap>
        {item.label}
      </Typography>
    </Box>
  );
};

// --- UnassignedItemsList ---
type UnassignedItemsListProps = {
  items: ContractTemplateUnassignedItemResult[];
  selectedNode: SelectedNode;
  onSelectNode: (node: SelectedNode) => void;
  onDelete: (item: ContractTemplateUnassignedItemResult) => void;
  onItemDrop: (
    itemKey: string,
    sourceGroupId: string | null,
    targetGroupId: string | null,
    targetIndex: number,
  ) => void;
};

const UnassignedItemsList: React.FC<UnassignedItemsListProps> = ({
  items,
  selectedNode,
  onSelectNode,
  onDelete,
  onItemDrop,
}) => {
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    try {
      const dragData = JSON.parse(e.dataTransfer.getData('text/plain'));
      onItemDrop(dragData.itemKey, dragData.sourceGroupId, null, 0);
    } catch {
      // ignore
    }
  };

  return (
    <Stack spacing={0} onDragOver={handleDragOver} onDrop={handleDrop} sx={{ minHeight: 24 }}>
      {items.map((item) => (
        <Box
          key={item.itemKey}
          draggable
          onDragStart={(e) => {
            e.dataTransfer.setData(
              'text/plain',
              JSON.stringify({ itemKey: item.itemKey, sourceGroupId: null }),
            );
          }}
          onClick={() => onSelectNode({ type: 'item', itemKey: item.itemKey, groupId: null })}
          sx={{
            display: 'flex',
            alignItems: 'center',
            pl: 1.5,
            pr: 1,
            py: 0.75,
            borderRadius: 1,
            cursor: 'pointer',
            bgcolor:
              selectedNode?.type === 'item' && selectedNode.itemKey === item.itemKey
                ? 'action.selected'
                : 'transparent',
            borderLeft:
              selectedNode?.type === 'item' && selectedNode.itemKey === item.itemKey ? 2 : 0,
            borderColor: 'primary.main',
            '&:hover': { bgcolor: 'action.hover' },
            transition: 'background-color 0.2s',
          }}
        >
          <DragIndicatorIcon sx={{ fontSize: 14, color: 'text.disabled', cursor: 'grab', mr: 1 }} />
          <TextFieldsIcon sx={{ fontSize: 14, color: 'text.secondary', mr: 1 }} />
          <Typography variant="body2" sx={{ flex: 1 }} noWrap>
            {item.label}
          </Typography>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(item);
            }}
            sx={{ color: 'text.disabled', '&:hover': { color: 'error.main' } }}
          >
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
    </Stack>
  );
};

// --- Right Pane ---
type RightPaneProps = {
  data: GetContractTemplateResponse;
  selectedNode: SelectedNode;
  onRefresh: () => void;
  onCancelNewItem: () => void;
};

const RightPane: React.FC<RightPaneProps> = ({
  data,
  selectedNode,
  onRefresh,
  onCancelNewItem,
}) => {
  if (!selectedNode) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          左のツリーからグループまたはアイテムを選択してください
        </Typography>
      </Box>
    );
  }

  if (selectedNode.type === 'group') {
    const group = data.results.find((g) => g.groupId === selectedNode.groupId);
    if (!group) return null;
    return <GroupEditPanel group={group} onRefresh={onRefresh} />;
  }

  if (selectedNode.type === 'new-item') {
    return (
      <ItemCreatePanel
        groupId={selectedNode.groupId}
        groupName={
          selectedNode.groupId
            ? data.results.find((g) => g.groupId === selectedNode.groupId)?.name || '不明'
            : '未所属'
        }
        onRefresh={onRefresh}
        onCancel={onCancelNewItem}
      />
    );
  }

  if (selectedNode.type === 'item') {
    // Find item in groups or unassigned
    let foundItem: ContractTemplateItemResult | ContractTemplateUnassignedItemResult | null = null;
    let parentGroup: ContractTemplateGroupResult | null = null;

    for (const g of data.results) {
      const item = g.items.find((i) => i.itemKey === selectedNode.itemKey);
      if (item) {
        foundItem = item;
        parentGroup = g;
        break;
      }
    }
    if (!foundItem) {
      const unassigned = data.unassignedItems.find((i) => i.itemKey === selectedNode.itemKey);
      if (unassigned) foundItem = unassigned;
    }

    if (!foundItem) return null;
    return <ItemEditPanel item={foundItem} parentGroup={parentGroup} onRefresh={onRefresh} />;
  }

  return null;
};

// --- GroupEditPanel ---
type GroupEditPanelProps = {
  group: ContractTemplateGroupResult;
  onRefresh: () => void;
};

const GroupEditPanel: React.FC<GroupEditPanelProps> = ({ group, onRefresh }) => {
  const isFixed = group.templateCategory === 'FIXED';
  const [name, setName] = useState(group.name);
  const [isVisible, setIsVisible] = useState(group.sortOrder >= 0);
  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    setName(group.name);
    setIsVisible(group.sortOrder >= 0);
  }, [group]);

  const handleUpdate = () => {
    const promises: Promise<void>[] = [];

    if (!isFixed && name !== group.name) {
      promises.push(updateContractTemplateGroup({ groupId: group.groupId, name }));
    }

    // If visibility changed, we need to update display
    const currentVisible = group.sortOrder >= 0;
    if (isVisible !== currentVisible) {
      // Need to fetch all groups and rebuild display
      getContractTemplate({ visibleOnly: false })
        .then((res) => {
          const visibleGroups = res.results
            .filter((g) => g.sortOrder >= 0 && g.groupId !== group.groupId)
            .sort((a, b) => a.sortOrder - b.sortOrder);

          let groups: { groupId: string; sortOrder: number }[];
          if (isVisible) {
            // Adding to visible: append at end
            groups = visibleGroups.map((g, i) => ({ groupId: g.groupId, sortOrder: i }));
            groups.push({ groupId: group.groupId, sortOrder: groups.length });
          } else {
            // Removing from visible
            groups = visibleGroups.map((g, i) => ({ groupId: g.groupId, sortOrder: i }));
          }
          return updateContractTemplateGroupDisplay({ groups });
        })
        .then(() => {
          successDefault('更新しました');
          onRefresh();
        })
        .catch((e: Error) => {
          errorDefault('更新に失敗しました', e.message);
        });
      return;
    }

    if (promises.length === 0) return;

    Promise.all(promises)
      .then(() => {
        successDefault('更新しました');
        onRefresh();
      })
      .catch((e: Error) => {
        errorDefault('更新に失敗しました', e.message);
      });
  };

  const activeItems = group.items
    .filter((i) => !i.isDeleted)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  // 編集タブの入力値を管理（タブ切り替えでもリセットされない）
  const [previewValues, setPreviewValues] = useState<Record<string, string>>({});
  const [techStackIds, setTechStackIds] = useState<string[]>([]);

  const handlePreviewValueChange = (itemKey: string, value: string) => {
    setPreviewValues((prev) => ({ ...prev, [itemKey]: value }));
  };

  return (
    <Box sx={{ p: 2.5 }}>
      <Stack spacing={2.5}>
        <TextField
          label="グループ名"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          fullWidth
          disabled={isFixed}
          inputProps={{ maxLength: 200 }}
        />

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">分類:</Typography>
          <Chip
            size="small"
            label={isFixed ? '固定項目' : '自由項目'}
            variant="outlined"
            sx={{ fontWeight: 600 }}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">表示:</Typography>
          <Switch
            checked={isVisible}
            onChange={(e) => setIsVisible(e.target.checked)}
            size="small"
          />
          <Typography variant="body2" color={isVisible ? 'success.main' : 'text.secondary'}>
            {isVisible ? 'ON' : 'OFF'}
          </Typography>
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={name === group.name && isVisible === group.sortOrder >= 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            更新
          </Button>
        </Box>

        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2 }}>
          <Tabs
            value={tabIndex}
            onChange={(_, v) => setTabIndex(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="編集" sx={{ textTransform: 'none' }} />
            <Tab label="プレビュー" sx={{ textTransform: 'none' }} />
          </Tabs>

          <Box sx={{ pt: 2 }}>
            {tabIndex === 0 && (
              <TemplateGroupView
                groups={[
                  {
                    groupId: group.groupId,
                    name: group.name,
                    items: activeItems.map((item) => ({
                      itemKey: item.itemKey,
                      label: item.label,
                      formType: item.formType,
                      selectItems: (item.selectItems || [])
                        .filter((si) => !si.isDeleted)
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((si) => ({ code: si.code, name: si.name })),
                    })),
                  },
                ]}
                mode="edit"
                values={previewValues}
                onValueChange={handlePreviewValueChange}
                techStackIds={techStackIds}
                onTechStackChange={setTechStackIds}
              />
            )}
            {tabIndex === 1 && (
              <TemplateGroupView
                groups={[
                  {
                    groupId: group.groupId,
                    name: group.name,
                    items: activeItems.map((item) => ({
                      itemKey: item.itemKey,
                      label: item.label,
                      formType: item.formType,
                      selectItems: (item.selectItems || [])
                        .filter((si) => !si.isDeleted)
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((si) => ({ code: si.code, name: si.name })),
                    })),
                  },
                ]}
                mode="preview"
                values={previewValues}
                onValueChange={handlePreviewValueChange}
                techStackIds={techStackIds}
                onTechStackChange={setTechStackIds}
              />
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

// --- ItemCreatePanel ---
type ItemCreatePanelProps = {
  groupId: string | null;
  groupName: string;
  onRefresh: () => void;
  onCancel: () => void;
};

const ItemCreatePanel: React.FC<ItemCreatePanelProps> = ({
  groupId,
  groupName,
  onRefresh,
  onCancel,
}) => {
  const [itemKey, setItemKey] = useState('');
  const [label, setLabel] = useState('');
  const [formType, setFormType] = useState('');
  const [selectItems, setSelectItems] = useState<CreateSelectItemInput[]>([]);

  const showSelectItems = SELECT_FORM_TYPES.includes(formType);

  const handleAddSelectItem = () => {
    setSelectItems((prev) => [...prev, { code: '', name: '', sortOrder: prev.length + 1 }]);
  };

  const handleRemoveSelectItem = (index: number) => {
    setSelectItems((prev) =>
      prev.filter((_, i) => i !== index).map((item, i) => ({ ...item, sortOrder: i + 1 })),
    );
  };

  const handleUpdateSelectItem = (index: number, field: 'code' | 'name', value: string) => {
    setSelectItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    );
  };

  const isValid = itemKey.trim() && label.trim() && formType && /^[a-zA-Z0-9_-]+$/.test(itemKey);

  const handleCreate = () => {
    createContractTemplateItem({
      itemKey: itemKey.trim(),
      label: label.trim(),
      formType,
      groupId: groupId || undefined,
      selectItems: showSelectItems && selectItems.length > 0 ? selectItems : undefined,
    })
      .then(() => {
        successDefault('登録しました');
        onRefresh();
      })
      .catch((e: Error) => {
        errorDefault('登録に失敗しました', e.message);
      });
  };

  return (
    <Box sx={{ p: 2.5 }}>
      <Stack spacing={2.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          item新規作成
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">所属グループ:</Typography>
          <Typography variant="body2" fontWeight={600}>
            {groupName}
          </Typography>
        </Stack>

        <TextField
          label="key"
          value={itemKey}
          onChange={(e) => setItemKey(e.target.value)}
          size="small"
          fullWidth
          helperText="半角英数字 + - + _ のみ"
          inputProps={{ maxLength: 100 }}
          error={!!itemKey && !/^[a-zA-Z0-9_-]*$/.test(itemKey)}
        />

        <TextField
          label="label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          size="small"
          fullWidth
          inputProps={{ maxLength: 200 }}
        />

        <FormControl size="small" fullWidth>
          <InputLabel>type</InputLabel>
          <Select value={formType} onChange={(e) => setFormType(e.target.value)} label="type">
            {FORM_TYPE_OPTIONS.map((opt) => (
              <MenuItem key={opt.code} value={opt.code}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showSelectItems && (
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" fontWeight={600}>
                select_items
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddSelectItem}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                追加
              </Button>
            </Stack>
            <Stack spacing={1}>
              {selectItems.map((si, idx) => (
                <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                  <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                  <TextField
                    size="small"
                    placeholder="code"
                    value={si.code}
                    onChange={(e) => handleUpdateSelectItem(idx, 'code', e.target.value)}
                    inputProps={{ maxLength: 50 }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    size="small"
                    placeholder="name"
                    value={si.name}
                    onChange={(e) => handleUpdateSelectItem(idx, 'name', e.target.value)}
                    inputProps={{ maxLength: 200 }}
                    sx={{ flex: 1 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveSelectItem(idx)}
                    sx={{ color: 'text.disabled' }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={onCancel} sx={{ textTransform: 'none', fontWeight: 600 }}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!isValid}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            登録
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

// --- ItemEditPanel ---
type ItemEditPanelProps = {
  item: ContractTemplateItemResult | ContractTemplateUnassignedItemResult;
  parentGroup: ContractTemplateGroupResult | null;
  onRefresh: () => void;
};

const ItemEditPanel: React.FC<ItemEditPanelProps> = ({ item, parentGroup, onRefresh }) => {
  const isFixed = parentGroup?.templateCategory === 'FIXED';
  const isDeleted = item.isDeleted;

  const [label, setLabel] = useState(item.label);
  const [editSelectItems, setEditSelectItems] = useState<UpdateSelectItemInput[]>([]);

  const formType = 'formType' in item ? item.formType : '';
  const showSelectItems = SELECT_FORM_TYPES.includes(formType);

  useEffect(() => {
    setLabel(item.label);
    const existing = (item.selectItems || [])
      .filter((si) => !si.isDeleted)
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((si) => ({
        selectItemId: si.selectItemId,
        code: si.code,
        name: si.name,
        sortOrder: si.sortOrder,
        isDeleted: false,
      }));
    const deleted = (item.selectItems || [])
      .filter((si) => si.isDeleted)
      .map((si) => ({
        selectItemId: si.selectItemId,
        code: si.code,
        name: si.name,
        sortOrder: si.sortOrder,
        isDeleted: true,
      }));
    setEditSelectItems([...existing, ...deleted]);
  }, [item]);

  const handleAddSelectItem = () => {
    const maxOrder = editSelectItems.filter((si) => !si.isDeleted).length;
    setEditSelectItems((prev) => [
      ...prev,
      { selectItemId: null, code: '', name: '', sortOrder: maxOrder + 1, isDeleted: false },
    ]);
  };

  const handleMarkDelete = (index: number) => {
    setEditSelectItems((prev) =>
      prev.map((si, i) => (i === index ? { ...si, isDeleted: true } : si)),
    );
  };

  const handleRestore = (index: number) => {
    setEditSelectItems((prev) =>
      prev.map((si, i) => (i === index ? { ...si, isDeleted: false } : si)),
    );
  };

  const handleUpdateSelectItemField = (index: number, field: 'name', value: string) => {
    setEditSelectItems((prev) =>
      prev.map((si, i) => (i === index ? { ...si, [field]: value } : si)),
    );
  };

  const handleUpdateNewCode = (index: number, value: string) => {
    setEditSelectItems((prev) => prev.map((si, i) => (i === index ? { ...si, code: value } : si)));
  };

  const handleUpdate = () => {
    updateContractTemplateItem({
      itemKey: item.itemKey,
      label,
      selectItems: showSelectItems ? editSelectItems : undefined,
    })
      .then(() => {
        successDefault('更新しました');
        onRefresh();
      })
      .catch((e: Error) => {
        errorDefault('更新に失敗しました', e.message);
      });
  };

  if (isDeleted) {
    return (
      <Box sx={{ p: 2.5 }}>
        <Typography variant="body2" color="text.secondary">
          このitemは削除済みです
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2.5 }}>
      <Stack spacing={2.5}>
        <Typography variant="subtitle1" fontWeight={600}>
          item編集
        </Typography>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">所属グループ:</Typography>
          <Typography variant="body2" fontWeight={600}>
            {parentGroup ? parentGroup.name : '未所属'}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">key:</Typography>
          <Typography variant="body2" fontWeight={600}>
            {item.itemKey}
          </Typography>
        </Stack>

        <TextField
          label="label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          size="small"
          fullWidth
          disabled={isFixed}
          inputProps={{ maxLength: 200 }}
        />

        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">type:</Typography>
          <Typography variant="body2" fontWeight={600}>
            {FORM_TYPE_OPTIONS.find((o) => o.code === formType)?.label || formType}
          </Typography>
        </Stack>

        {showSelectItems && (
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Typography variant="body2" fontWeight={600}>
                select_items
              </Typography>
              {!isFixed && (
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddSelectItem}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  追加
                </Button>
              )}
            </Stack>
            <Stack spacing={0.5}>
              {editSelectItems.map((si, idx) => {
                const isExisting = !!si.selectItemId;
                const isMarkedDeleted = si.isDeleted;

                if (isMarkedDeleted && isExisting) {
                  // Pending delete or already deleted
                  return (
                    <Stack
                      key={idx}
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ opacity: 0.5, py: 0.5, px: 1 }}
                    >
                      <Box sx={{ width: 26 }} />
                      <Typography variant="body2" sx={{ flex: 1, textDecoration: 'line-through' }}>
                        {si.code}
                      </Typography>
                      <Typography variant="body2" sx={{ flex: 1, textDecoration: 'line-through' }}>
                        {si.name}
                      </Typography>
                      {!isFixed && (
                        <IconButton
                          size="small"
                          onClick={() => handleRestore(idx)}
                          sx={{ color: 'primary.main' }}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                  );
                }

                if (isMarkedDeleted) return null;

                return (
                  <Stack key={idx} direction="row" alignItems="center" spacing={1}>
                    <DragIndicatorIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    {isExisting ? (
                      <Typography variant="body2" sx={{ flex: 1, px: 1 }}>
                        {si.code}
                      </Typography>
                    ) : (
                      <TextField
                        size="small"
                        placeholder="code"
                        value={si.code}
                        onChange={(e) => handleUpdateNewCode(idx, e.target.value)}
                        inputProps={{ maxLength: 50 }}
                        sx={{ flex: 1 }}
                        disabled={isFixed}
                      />
                    )}
                    <TextField
                      size="small"
                      placeholder="name"
                      value={si.name}
                      onChange={(e) => handleUpdateSelectItemField(idx, 'name', e.target.value)}
                      inputProps={{ maxLength: 200 }}
                      sx={{ flex: 1 }}
                      disabled={isFixed}
                    />
                    {!isFixed && (
                      <IconButton
                        size="small"
                        onClick={() => handleMarkDelete(idx)}
                        sx={{ color: 'text.disabled' }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}

        {!isFixed && (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleUpdate}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              更新
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default TemplateManagement;
